import { useState } from 'react';
import { BellRing, Loader2, LockKeyhole, User } from 'lucide-react';
import { login, type SessionUser } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FieldWrapper } from '@/components/ui/field-wrapper';

export function LoginPage({ onLoggedIn }: { onLoggedIn: (u: SessionUser) => void }) {
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const user = await login(loginId, password);
      onLoggedIn(user);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
      <div className="w-full max-w-[400px] px-4">
        <div className="flex flex-col items-center mb-8">
          <div className="w-12 h-12 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-4 border border-[#E2E8F0]">
            <BellRing size={24} className="text-[#008ED3]" />
          </div>
          <h1 className="text-xl font-bold text-[#0F172A]">Notification Admin</h1>
          <p className="text-[13px] text-[#64748B] mt-1 text-center">
            Sign in to manage OM365 notifications
          </p>
        </div>

        <Card className="shadow-[0_8px_30px_rgb(0,0,0,0.04)] border-[#E2E8F0]">
          <CardHeader className="hidden">
            <CardTitle>Sign In</CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            <form onSubmit={submit} className="space-y-5">
              <FieldWrapper label="Username / Login ID" required>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[#9CA3AF] pointer-events-none" />
                  <Input
                    type="text"
                    value={loginId}
                    onChange={(e) => setLoginId(e.target.value)}
                    required
                    placeholder="Enter your username"
                    className="pl-10"
                    autoFocus
                  />
                </div>
              </FieldWrapper>

              <FieldWrapper label="Password" required>
                <div className="relative">
                  <LockKeyhole className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[#9CA3AF] pointer-events-none" />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    className="pl-10"
                  />
                </div>
              </FieldWrapper>

              {error && (
                <p className="text-[12px] text-[#EF4444] bg-[#FEF2F2] p-2.5 rounded-lg border border-[#FEE2E2]">
                  {error}
                </p>
              )}

              <Button
                type="submit"
                disabled={busy}
                className="w-full h-11 text-[14px] font-semibold"
              >
                {busy ? (
                  <>
                    <Loader2 size={16} className="animate-spin mr-2" />
                    Signing in…
                  </>
                ) : (
                  'Sign in'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="mt-8 text-center text-[12px] text-[#94A3B8]">
          &copy; {new Date().getFullYear()} Cognisun. Admin role required.
        </p>
      </div>
    </div>
  );
}
